import { JSX } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { RootState } from "../redux/store";
import { ROUTES } from "./constants";
import { Layout } from "../components/Layout/Layout";

interface PrivateRouteProps {
  children: JSX.Element;
}

export function PrivateRoute({ children }: PrivateRouteProps) {
  const { authenticated, user } = useSelector((state: RootState) => state.userInfo);
  const token = localStorage.getItem('token');
  const location = useLocation();

  if (!authenticated || !token) {
    return <Navigate to={ROUTES.SIGNIN} replace />;
  }

  const pathname = location.pathname.toLowerCase();
  const isUserRole = user?.role?.toLowerCase() === "user";

  // Se o usuário é "user" e não tem perfil completo, redirecionar para a página de perfil
  // Exceto se já estiver na página de perfil
  // Considera perfil completo se existe e tem pelo menos um campo preenchido (address) ou isValidated !== false
  const hasProfile = user?.profile && (user.profile.address || user.profile.isValidated !== false);
  const hasIncompleteProfile = isUserRole && !hasProfile;
  const isOnUserProfile = pathname === ROUTES.USER_PROFILE.toLowerCase();

  if (hasIncompleteProfile && !isOnUserProfile) {
    return <Navigate to={ROUTES.USER_PROFILE} replace state={{ from: location }} />;
  }

  // Usuário do tipo "user" não pode acessar Dashboard, Agenda, Relatórios, Auditoria ou Financeiro
  const restrictedForUser = [
    ROUTES.DASHBOARD,
    ROUTES.SCHEDULE,
    ROUTES.REPORTS,
    ROUTES.AUDIT_LOGS,
    ROUTES.FINANCE,
  ];
  const isRestrictedPathForUser = restrictedForUser.some((basePath) => {
    const base = basePath.toLowerCase();
    return pathname === base || pathname.startsWith(base + "/");
  });
  if (isUserRole && isRestrictedPathForUser) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  // A tela de relatórios e auditoria só pode ser acessada por ADMIN, OWNER e DOCTOR_OWNER
  const allowedAdminLikeRoles = ["ADMIN", "OWNER", "DOCTOR_OWNER"];
  const userRoleUpper = String(user?.role || "").toUpperCase();
  const canAccessAdminLike = allowedAdminLikeRoles.includes(userRoleUpper);

  // Financeiro: ADMIN, OWNER, DOCTOR_OWNER e FINANCIAL
  const allowedFinanceRoles = ["ADMIN", "OWNER", "DOCTOR_OWNER", "FINANCIAL"];
  const canAccessFinance = allowedFinanceRoles.includes(userRoleUpper);

  const reportsBase = ROUTES.REPORTS.toLowerCase();
  const isReportsPath = pathname === reportsBase || pathname.startsWith(reportsBase + "/");

  if (!canAccessAdminLike && isReportsPath) {
    return <Navigate to={isUserRole ? ROUTES.HOME : ROUTES.DASHBOARD} replace />;
  }

  const auditBase = ROUTES.AUDIT_LOGS.toLowerCase();
  const isAuditPath = pathname === auditBase || pathname.startsWith(auditBase + "/");

  if (!canAccessAdminLike && isAuditPath) {
    return <Navigate to={isUserRole ? ROUTES.HOME : ROUTES.DASHBOARD} replace />;
  }

  const financeBase = ROUTES.FINANCE.toLowerCase();
  const isFinancePath = pathname === financeBase || pathname.startsWith(financeBase + "/");

  if (!canAccessFinance && isFinancePath) {
    return <Navigate to={isUserRole ? ROUTES.HOME : ROUTES.DASHBOARD} replace />;
  }

  // Meus Prontuários / Gerenciar Modelos: exclusivo de DOCTOR e DOCTOR_OWNER
  const allowedDoctorOnlyRoles = ["DOCTOR", "DOCTOR_OWNER"];
  const canAccessMyMedicalRecords = allowedDoctorOnlyRoles.includes(userRoleUpper);
  const myRecordsBase = ROUTES.MY_MEDICAL_RECORDS.toLowerCase();
  const isMyRecordsPath = pathname === myRecordsBase || pathname.startsWith(myRecordsBase + "/");

  if (!canAccessMyMedicalRecords && isMyRecordsPath) {
    return <Navigate to={isUserRole ? ROUTES.HOME : ROUTES.DASHBOARD} replace />;
  }

  // Para usuários que NÃO são do tipo "user", o Dashboard passa a ser a home
  if (!isUserRole && pathname === ROUTES.HOME.toLowerCase()) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <Layout>{children}</Layout>;
}
